import BenchlingConfig from 'BenchlingConfig';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

// Define UI order for different chat types.
// GraphQL enums don't preserve order, so we define it explicitly here.
export const ORDERED_CHAT_TYPES = [
  ChatType.DEEP_RESEARCH,
  ChatType.DATA_CAPTURE,
  ChatType.LEGACY_DATA_IMPORT,
];

export function isAISidepanelEnabled(): boolean {
  return getChatTypes().length > 0;
}

export function getChatTypes(): Array<ChatType> {
  return ORDERED_CHAT_TYPES.filter((chatType) => isChatTypeEnabled(chatType));
}

export function isChatTypeEnabled(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case ChatType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case ChatType.LEGACY_DATA_IMPORT:
      // LEGACY_DATA_IMPORT is accessed via dedicated route, not shown in regular chat list
      return false;
    default:
      return false;
  }
}

/**
 * Parse the value out of a URL like/chats/new?agent=<agentTypeQSValue> (see: AIRoutes.ts).
 *
 * NOTE: These URLs may be put in marketing emails, so we should avoid changing the string values
 * when possible, i.e. maintain backwards compatibility. It is acceptable to have multiple querystring
 * values map to the same AgentType.
 */
export function getAgentTypeFromQueryStringValue(agentTypeQSValue: string | null): AgentType | null {
  switch (agentTypeQSValue) {
    case 'ask':
      return AgentType.ASK;
    case 'deep-research':
      return AgentType.DEEP_RESEARCH;
    case 'compose':
      return AgentType.DATA_CAPTURE;
    default:
      return null;
  }
}

export function getChatTypeText(chatType: ChatType): string {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return 'deep research';
    case ChatType.DATA_CAPTURE:
      return 'Compose';
    case ChatType.LEGACY_DATA_IMPORT:
      return 'Legacy Data Import';
    default:
      throw new Error(`Unknown chat type: ${assertNever(chatType)}`);
  }
}

export function doesChatTypePreferExpandedSidepanel(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DATA_CAPTURE:
    case ChatType.LEGACY_DATA_IMPORT:
      return true;
    case ChatType.DEEP_RESEARCH:
      return false;
    default:
      assertNever(chatType);
      return false;
  }
}

export const ChatTypeByAgentType: Record<AgentType, ChatType> = {
  [AgentType.DEEP_RESEARCH]: ChatType.DEEP_RESEARCH,
  [AgentType.ASK]: ChatType.DEEP_RESEARCH,
  [AgentType.DATA_CAPTURE]: ChatType.DATA_CAPTURE,
  [AgentType.LEGACY_DATA_IMPORT]: ChatType.LEGACY_DATA_IMPORT,
};

export const AGENT_TYPE_ORDERING: Record<AgentType, number> = {
  [AgentType.ASK]: 0,
  [AgentType.DEEP_RESEARCH]: 1,
  [AgentType.DATA_CAPTURE]: 2,
  [AgentType.LEGACY_DATA_IMPORT]: 3,
};

const ORDERED_AGENT_TYPES = (Object.values(AgentType) as Array<AgentType>).sort(
  (left, right) => AGENT_TYPE_ORDERING[left] - AGENT_TYPE_ORDERING[right]
);

export function getAgentTypeIcon(agentType: AgentType): BenchlingIconName | null {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'file-magnifying-glass';
    case AgentType.DATA_CAPTURE:
      return 'pen';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'import';
    case AgentType.ASK:
      return 'comments';
    default:
      assertNever(agentType);
      return null;
  }
}

export function getAgentTypeText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Deep Research';
    case AgentType.ASK:
      return 'Ask';
    case AgentType.DATA_CAPTURE:
      return 'Compose';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Data Import';
    default:
      assertNever(agentType);
      throw new Error(`Unknown agent type: ${assertNever(agentType)}`);
  }
}

export function isAgentTypeEnabled(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    case AgentType.DATA_CAPTURE:
      return BenchlingConfig.LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT;
    case AgentType.LEGACY_DATA_IMPORT:
      // LEGACY_DATA_IMPORT is accessed via dedicated route, not shown in sidepanel or agent toolbar
      return false;
    case AgentType.ASK:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH;
    default:
      assertNever(agentType);
      return false;
  }
}

export function getAvailableAgentTypes(chatTypes: Array<ChatType>): Array<AgentType> {
  // When agent switching is enabled, all enabled agents are available regardless of the current
  // chat type. This allows users to switch between agents (e.g., Deep Research -> Compose) within
  // the same thread. Backend compatibility logic handles the different invariants each agent has.
  if (BenchlingConfig.LLM_ENABLE_AGENT_SWITCH) {
    return ORDERED_AGENT_TYPES.filter((agentType) => isAgentTypeEnabled(agentType));
  }
  return ORDERED_AGENT_TYPES.filter((agentType) => {
    // Special case: LDI is only available when explicitly in an LDI chat
    if (agentType === AgentType.LEGACY_DATA_IMPORT) {
      return chatTypes.includes(ChatType.LEGACY_DATA_IMPORT);
    }
    return isAgentTypeEnabled(agentType) && chatTypes.includes(ChatTypeByAgentType[agentType]);
  });
}

/**
 * Get the default agent type for new chats.
 *
 * If initialAgentType is provided and available, it will be used.
 * Otherwise, defaults to Ask if available, or the first available agent type.
 */
export function getDefaultAgentType(initialAgentType?: AgentType | null): AgentType | null {
  const availableAgentTypes = getAvailableAgentTypes(getChatTypes());
  if (initialAgentType && availableAgentTypes.includes(initialAgentType)) {
    return initialAgentType;
  }
  // Default to Ask for new chats
  return availableAgentTypes.includes(AgentType.ASK) ? AgentType.ASK : (availableAgentTypes[0] ?? null);
}

export function getAgentTypePlaceholderText(agentType: AgentType): string {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return 'Write detailed reports based on your data';
    case AgentType.ASK:
      return 'Ask questions about your data';
    case AgentType.DATA_CAPTURE:
      return 'Draft an entry or template';
    case AgentType.LEGACY_DATA_IMPORT:
      return 'Transcribe a file into a Benchling document';
    default:
      assertNever(agentType);
      return '';
  }
}

/**
 * doesAgentTypeSupportSources must also return true to allow public data to be used.
 */
export function doesAgentTypeSupportPublicData(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return BenchlingConfig.LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA;
    case AgentType.ASK:
      return false;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the agent backend supports setting CreateDisplayMessageChatEvent['sources'].
 */
export function doesAgentTypeSupportSources(agentType: AgentType): boolean {
  switch (agentType) {
    case AgentType.DEEP_RESEARCH:
      return true;
    case AgentType.DATA_CAPTURE:
      return false;
    case AgentType.ASK:
      return false;
    case AgentType.LEGACY_DATA_IMPORT:
      return false;
    default:
      assertNever(agentType);
      return false;
  }
}

/**
 * Indicate if the chat type should show progress UI (tool calls, activity tracking).
 *
 * TODO: This should be unconditionally true.
 */
export function doesChatTypeShowProgress(chatType: ChatType): boolean {
  switch (chatType) {
    case ChatType.DEEP_RESEARCH:
      return true;
    case ChatType.DATA_CAPTURE:
      return true;
    case ChatType.LEGACY_DATA_IMPORT:
      return true;
    default:
      assertNever(chatType);
      return false;
  }
}
